import { useCallback } from "react";

import { IHttpRequestConfig } from "@/browser";

import { useFetcher } from "../../Fetcher/useFetcher";
import { getUniqueKey } from "../service";
import {
  IPageable,
  IPageableFetcherItemRenderProps,
  IPageableFetcherRenderProps,
  IPageMeta,
  TPageableFetchFunction,
} from "../types";

interface IMeta<F, S> {
  meta: IPageMeta<F, S>;
  changeMeta: (state: IPageMeta<F, S>) => void;
}

interface State<R, M extends IPageable<R[]>, F, S> extends IPageMeta<F, S>, IPageableFetcherRenderProps<R, M> {}

/**
 * Accept async pageable fetch function that will be handled in effect
 *
 * @template R Return type
 * @template M Response meta type
 * @template F Filter config type
 * @template S Sort config type
 * @param {TPageableFetchFunction<R, M>} fetchFunction function that handle pageable fetch
 * @param {IMeta<F,S>} meta object with meta obj and changeMeta function
 * @param {any[]} deps hook deps
 * @returns {State<R, M>}
 */

export const useMetaPageableFetcher = <R, M extends IPageable<R[]>, F, S>(
  fetchFunction: TPageableFetchFunction<R, M, F, S>,
  { meta, changeMeta }: IMeta<F, S>,
  deps: any[] = [],
): State<R, M, F, S> => {
  const fetchData = async (params: IPageMeta<F, S>, options: IHttpRequestConfig) => {
    const result = await fetchFunction(params, options);

    const payload = {
      ...result,
      content: result.content.map((data: R) => ({ id: getUniqueKey(), data })),
    };

    return payload;
  };

  const singleFetcher = useFetcher(fetchData, meta, deps);

  const handlePageChange = useCallback(
    (page: number) => {
      const newMeta = { ...meta, page };
      changeMeta(newMeta);
    },
    [meta, changeMeta],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      const newMeta = { ...meta, page: 0, size };
      changeMeta(newMeta);
    },
    [meta, changeMeta],
  );

  const handleFilterChange = useCallback(
    (filtersConfig: F) => {
      const newMeta = {
        ...meta,
        page: 0,
        filtersConfig,
      };
      changeMeta(newMeta);
    },
    [meta, changeMeta],
  );

  const handleSortChange = useCallback(
    (sortConfig: S) => {
      const newMeta = {
        ...meta,
        page: 0,
        sortConfig,
      };
      changeMeta(newMeta);
    },
    [meta, changeMeta],
  );

  const handleUpdateRecord = useCallback(
    (record: IPageableFetcherItemRenderProps<R>) => {
      const { payload } = singleFetcher;

      if (!payload) {
        return;
      }

      const content = payload.content.map((cp: IPageableFetcherItemRenderProps<R>) =>
        cp.id === record.id ? record : cp,
      );
      singleFetcher.updatePayload({ ...payload, content });
    },
    [singleFetcher.payload],
  );

  return {
    ...singleFetcher,
    changeFilter: handleFilterChange,
    changePage: handlePageChange,
    changePageSize: handlePageSizeChange,
    changeSort: handleSortChange,
    updateRecord: handleUpdateRecord,
  };
};
